import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { FileDown } from "lucide-react";
import { Header } from "../components/Header";
import { ContactFooter } from "../components/ContactFooter";
import { AssistantWidget } from "../components/AssistantWidget";
import { PageBanner, PageHeading } from "../components/PageBanner";
import { useLanguage } from "../i18n";
import { LEGAL } from "../legal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Rendered pages are cached for a day under a fixed URL; bump this when the stored renders are regenerated so browsers fetch the new ones
const PAGE_V = 2;

// "BOOK LIGHTING PROJECT-Residenza Rossi" -> kind "Book Lighting Project", name "Residenza Rossi"
const splitTitle = (title = "") => {
  const m = title.match(/^(.+?)\s*(?:-|–|—)\s*(.+)$/);
  if (!m) return { kind: "", name: title };
  const kind = m[1].trim();
  return { kind: kind === kind.toUpperCase() ? kind.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase()) : kind, name: m[2].trim() };
};
// Card / banner picture: a photo taken from the book at full resolution when there is one, else a page of the book
const photoSrc = (it) => (it.photos > 0 && it.banner_mode !== "page" ? `${API}/portfolio/${it.id}/photo/${Math.min(it.banner_photo || 1, it.photos)}?v=${it.photos_v || 1}` : `${API}/portfolio/${it.id}/page/${it.cover_page || 1}?v=${PAGE_V}`);
const bannerSrc = (it) => {
  if (it.banner_mode === "plan" && it.plan_page) return `${API}/portfolio/${it.id}/plan?v=${it.plan_v || 0}`;
  if (it.banner_mode === "page") return `${API}/portfolio/${it.id}/page/${it.cover_page || 1}?v=${PAGE_V}`;
  return photoSrc(it);
};
const FACT_KEYS = ["type", "year", "city", "area"];
const factsOf = (it) => FACT_KEYS.map((k) => (it?.facts || {})[k]).filter(Boolean);

const WipBadge = ({ t, dark }) => (
  <span data-testid="project-wip" className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded-sm ${dark ? "bg-black/55 text-white/90" : "bg-amberl/20 text-amberdark"}`}>
    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />{t.projectsShow.wip}
  </span>
);

const ProjectCard = ({ it, t }) => {
  const { kind, name } = splitTitle(it.title);
  const facts = factsOf(it);
  const label = it.kind === "example" ? (kind || t.projectsShow.exampleKind) : (facts[0] || t.projectsShow.kind);
  return (
    <Link to={`/progetti/${it.id}`} data-testid={`project-card-${it.id}`} className="group block">
      <div className="relative aspect-[4/5] overflow-hidden bg-black">
        <img src={photoSrc(it)} alt={name} loading="lazy" draggable={false} className="bw-hover w-full h-full object-cover group-hover:scale-[1.02]" />
        {it.wip && <span className="absolute top-3 left-3"><WipBadge t={t} dark /></span>}
      </div>
      <h3 className="mt-4 text-xl text-ink leading-snug">{name}</h3>
      <p className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.12em] text-inksoft">{label}</p>
    </Link>
  );
};

const Grid = ({ items, t, testid }) => (
  <div data-testid={testid} className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
    {items.map((it) => <ProjectCard key={it.id} it={it} t={t} />)}
  </div>
);

export function ProjectsPage() {
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/portfolio`).then((r) => r.json()).then(setItems).catch(() => setItems([]));
  }, []);
  const realized = (items || []).filter((x) => x.kind !== "example");
  const examples = (items || []).filter((x) => x.kind === "example");
  const ps = t.projectsShow;
  return (
    <div data-testid="projects-page" className="min-h-screen bg-cream" onContextMenu={(e) => e.preventDefault()}>
      <Header />
      <PageHeading testid="projects-heading" label={t.nav.projects} title={ps.realizedTitle} subtitle={ps.realizedSubtitle} />
      <PageBanner testid="projects-banner" label={t.nav.projects} rgb mid noCaption ratio="16 / 9" images={[{ src: "/videos/luce-fondatrici-poster.jpg", pos: "50% 40%" }]} />
      <main className="pt-10 pb-24 max-w-7xl mx-auto px-6 lg:px-12">
        <p data-testid="projects-wip-note" className="mt-6 inline-flex items-start gap-3 max-w-2xl border border-amberl/50 bg-white/60 rounded-sm px-4 py-3 text-sm text-inksoft">
          <span className="mt-1.5 w-1.5 h-1.5 shrink-0 rounded-full bg-amberdark animate-pulse" />{ps.wipNote}
        </p>
        {items && items.length === 0 && <p data-testid="projects-empty" className="mt-14 text-sm text-inksoft">{t.projects.empty}</p>}
        {realized.length > 0 && (
          <section data-testid="projects-realized" className="mt-16">
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-amberdark">{ps.realizedOverline}</p>
            <Grid items={realized} t={t} testid="project-list" />
          </section>
        )}
        {examples.length > 0 && (
          <section data-testid="projects-examples" className="mt-24 pt-14 border-t border-ink/10">
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-amberdark">{ps.examplesOverline}</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight text-ink">{ps.examplesTitle}</h2>
            <p className="mt-4 text-sm text-inksoft max-w-2xl">{ps.examplesSubtitle}</p>
            <Grid items={examples} t={t} testid="project-examples-list" />
          </section>
        )}
      </main>
      <ContactFooter clearWidget />
      <AssistantWidget />
    </div>
  );
}

export function ProjectDetailPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!items) fetch(`${API}/portfolio`).then((r) => r.json()).then(setItems).catch(() => setItems([]));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const it = (items || []).find((x) => x.id === id);
  if (items && !it) return <Navigate to="/progetti" replace />;
  const { kind, name } = splitTitle(it?.title);
  const facts = factsOf(it);
  const isPlan = it?.banner_mode === "plan" && it?.plan_page;
  const others = (items || []).filter((x) => x.id !== id && x.kind === it?.kind).slice(0, 3);
  return (
    <div data-testid="project-detail-page" className="min-h-screen bg-cream" onContextMenu={(e) => e.preventDefault()}>
      <Header />
      <main>
        <section data-testid="project-banner" className={`day-light-bg group relative mt-14 h-[51vh] min-h-[345px] overflow-hidden ${isPlan ? "bg-[#111]" : "bg-black"}`}>
          {it && (isPlan
            ? <img data-testid="project-banner-plan" src={bannerSrc(it)} alt={name} draggable={false} className="absolute right-0 top-0 h-full w-full lg:w-1/2 object-contain p-8 lg:p-12 opacity-90" />
            : <img src={bannerSrc(it)} alt={name} draggable={false} className="bw-hover absolute inset-0 w-full h-full object-cover object-[50%_40%]" />)}
          <div className={`pbn-shade ${isPlan ? "pbn-shade-plan" : ""} absolute inset-0 ${isPlan ? "bg-gradient-to-r from-[#111] via-[#111]/70 to-transparent" : "bg-gradient-to-t from-black/80 via-black/30 to-black/10"}`} />
          <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-14">
            <p className="pbn-label font-mono text-[11px] uppercase tracking-[0.15em] text-white/70">
              <Link to="/" className="hover:text-white">Home</Link> · <Link to="/progetti" className="hover:text-white">{t.nav.projects}</Link>{kind ? ` · ${kind}` : ""}
            </p>
            <h1 data-testid="project-title" className="pbn-title mt-4 font-display text-5xl sm:text-6xl tracking-tight text-white">{name}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
              <p data-testid="project-facts" className="pbn-label font-mono text-[11px] uppercase tracking-[0.15em] text-white/85 whitespace-pre">
                {facts.length ? facts.join("   ") : (it?.kind === "example" ? t.projectsShow.exampleKind : t.projectsShow.kind)}
              </p>
              {it?.wip && <WipBadge t={t} dark />}
            </div>
          </div>
        </section>
        {it && it.kind !== "example" && it.photos > 1 && (
          <section data-testid="project-photos" className="py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 grid sm:grid-cols-2 gap-6 lg:gap-8">
              {Array.from({ length: it.photos }, (_, i) => (
                <div key={i} className="group overflow-hidden bg-black">
                  <img src={`${API}/portfolio/${it.id}/photo/${i + 1}?v=${it.photos_v || 1}`} alt={`${name} — ${i + 1}`} loading="lazy" draggable={false} className="bw-hover w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </section>
        )}
        {it && (
          <section className="day-light-bg book-sec py-16 lg:py-20 bg-black">
            <div className="max-w-5xl mx-auto px-6 lg:px-12">
              <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <h2 className="font-display text-3xl text-white">{t.projectsShow.book}</h2>
                <a data-testid={`project-download-${it.id}`} href={`${API}/portfolio/${it.id}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white rounded-sm px-4 h-10 font-mono text-[11px] uppercase tracking-[0.12em]"><FileDown className="w-4 h-4" />{t.projects.download}</a>
              </div>
              <div data-testid="project-viewer" className="relative space-y-6 select-none">
                {Array.from({ length: it.pages }, (_, i) => (
                  <img key={`${it.id}-${i}`} data-testid={`project-page-${i + 1}`} src={`${API}/portfolio/${it.id}/page/${i + 1}?v=${PAGE_V}`} alt={`${name} — ${i + 1}`} loading={i < 2 ? "eager" : "lazy"} draggable={false} className="w-full rounded-sm pointer-events-none" />
                ))}
                <div className="pointer-events-none absolute inset-0 flex items-start justify-center pt-[20vh]">
                  <span className="sticky top-1/2 font-display text-3xl sm:text-5xl text-white/25 -rotate-12 select-none tracking-widest">{t.projects.watermark}</span>
                </div>
              </div>
              <p data-testid="projects-protection-note" className="mt-8 text-[11px] leading-relaxed text-white/50">{t.projects.protection}</p>
            </div>
          </section>
        )}
        {others.length > 0 && (
          <section className="py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
              <Link to="/progetti" data-testid="project-back" className="font-mono text-[11px] uppercase tracking-[0.15em] text-inksoft hover:text-ink">&larr; {t.projectsShow.back}</Link>
              <Grid items={others} t={t} testid="project-others" />
            </div>
          </section>
        )}
      </main>
      <ContactFooter clearWidget />
      <AssistantWidget />
    </div>
  );
}

export function LegalPage() {
  const { slug } = useParams();
  const doc = LEGAL[slug] || LEGAL.privacy;
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  return (
    <div data-testid={`legal-page-${slug}`} className="min-h-screen bg-cream">
      <Header />
      <main className="pt-32 pb-24 max-w-3xl mx-auto px-6">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amberdark mb-6">FArLight — Legale</p>
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-ink">{doc.title}</h1>
        <p className="mt-3 text-xs text-inksoft">Ultimo aggiornamento {doc.updated} — Bozza da completare con i dati societari (ragione sociale, P.IVA, sede legale).</p>
        <div className="mt-10 space-y-8">
          {doc.sections.map((s, i) => (
            <section key={i}><h2 className="font-display text-2xl text-ink">{s.h}</h2><p className="mt-3 text-sm leading-relaxed text-inksoft whitespace-pre-line">{s.p}</p></section>
          ))}
        </div>
        <div className="mt-12 flex gap-4 text-xs font-semibold uppercase tracking-[0.15em]">
          {Object.keys(LEGAL).map((k) => <Link key={k} to={`/legal/${k}`} className={k === slug ? "text-amberdark" : "text-inksoft hover:text-ink"}>{LEGAL[k].title}</Link>)}
        </div>
      </main>
      <ContactFooter />
    </div>
  );
}
